/**
 * Development logging utility for Kibu Shop
 * Replaces console.log statements with controlled logging that can be disabled in production
 */

export type LogLevel = 'debug' | 'info' | 'warn' | 'error'
export type LogContext = 'analytics' | 'api' | 'component' | 'store' | 'general'

interface LogConfig {
    enabled: boolean
    level: LogLevel
    contexts: LogContext[]
}

class Logger {
    private config: LogConfig = {
        enabled: this.parseEnabled(),
        level: this.parseLevel(),
        contexts: this.parseContexts()
    }

    private parseEnabled(): boolean {
        // Check explicit environment variable first
        if (process.env.REACT_APP_LOGGING_ENABLED !== undefined) {
            return process.env.REACT_APP_LOGGING_ENABLED === 'true'
        }
        // Fallback to NODE_ENV check
        return process.env.NODE_ENV === 'development'
    }

    private parseLevel(): LogLevel {
        const envLevel = process.env.REACT_APP_LOGGING_LEVEL as LogLevel
        const validLevels: LogLevel[] = ['debug', 'info', 'warn', 'error']
        return validLevels.includes(envLevel) ? envLevel : 'debug'
    }

    private parseContexts(): LogContext[] {
        const envContexts = process.env.REACT_APP_LOGGING_CONTEXTS
        if (!envContexts) return []
        
        const contexts = envContexts.split(',').map(ctx => ctx.trim() as LogContext)
        const validContexts: LogContext[] = ['analytics', 'api', 'component', 'store', 'general']
        return contexts.filter(ctx => validContexts.includes(ctx))
    }

    private shouldLog(level: LogLevel, context: LogContext): boolean {
        if (!this.config.enabled) return false
        if (!this.config.contexts.includes(context)) return false
        
        const levels = ['debug', 'info', 'warn', 'error']
        const currentLevelIndex = levels.indexOf(this.config.level)
        const messageLevelIndex = levels.indexOf(level)
        
        return messageLevelIndex >= currentLevelIndex
    }

    private formatMessage(context: LogContext, message: string, data?: any): any[] {
        const timestamp = new Date().toISOString().substr(11, 8)
        const prefix = `[${timestamp}] [${context.toUpperCase()}]`
        
        return data ? [prefix, message, data] : [prefix, message]
    }

    debug(context: LogContext, message: string, data?: any): void {
        if (this.shouldLog('debug', context)) {
            console.log(...this.formatMessage(context, message, data))
        }
    }

    info(context: LogContext, message: string, data?: any): void {
        if (this.shouldLog('info', context)) {
            console.info(...this.formatMessage(context, message, data))
        }
    }

    warn(context: LogContext, message: string, data?: any): void {
        if (this.shouldLog('warn', context)) {
            console.warn(...this.formatMessage(context, message, data))
        }
    }

    error(context: LogContext, message: string, data?: any): void {
        if (this.shouldLog('error', context)) {
            console.error(...this.formatMessage(context, message, data))
        }
    }

    // Analytics specific logging
    analytics = {
        init: (service: string, id?: string) => {
            this.info('analytics', `${service} initializing${id ? ` with ID: ${id}` : ''}`)
        },
        initialized: (service: string) => {
            this.info('analytics', `${service} initialized`)
        },
        already: (service: string) => {
            this.debug('analytics', `${service} already initialized`)
        },
        denied: (service: string) => {
            this.info('analytics', `${service} not initializing - consent not granted`)
        },
        consent: (service: string, action: 'grant' | 'revoke') => {
            this.info('analytics', `${service} consent ${action}ed`)
        },
        removed: (service: string) => {
            this.info('analytics', `${service} elements removed`)
        }
    }

    // API specific logging
    api = {
        request: (endpoint: string, method = 'GET') => {
            this.debug('api', `${method} ${endpoint}`)
        },
        response: (endpoint: string, status: number) => {
            this.debug('api', `${endpoint} responded with ${status}`)
        },
        error: (endpoint: string, error: any) => {
            this.error('api', `${endpoint} failed`, error)
        },
        cache: (key: string, action: 'hit' | 'miss' | 'set') => {
            this.debug('api', `Cache ${action}: ${key}`)
        }
    }

    // Component specific logging
    component = {
        mount: (name: string) => {
            this.debug('component', `${name} mounted`)
        },
        unmount: (name: string) => {
            this.debug('component', `${name} unmounted`)
        },
        render: (name: string, reason?: string) => {
            this.debug('component', `${name} rendered${reason ? ` (${reason})` : ''}`)
        },
        state: (name: string, change: string) => {
            this.debug('component', `${name} state: ${change}`)
        }
    }

    // Store specific logging
    store = {
        action: (store: string, action: string, payload?: any) => {
            this.debug('store', `${store}: ${action}`, payload)
        },
        state: (store: string, state: any) => {
            this.debug('store', `${store} state updated`, state)
        }
    }

    // Configuration inspection (for debugging)
    getConfig() {
        return {
            ...this.config,
            environment: process.env.NODE_ENV,
            rawEnvVars: {
                REACT_APP_LOGGING_ENABLED: process.env.REACT_APP_LOGGING_ENABLED,
                REACT_APP_LOGGING_LEVEL: process.env.REACT_APP_LOGGING_LEVEL,
                REACT_APP_LOGGING_CONTEXTS: process.env.REACT_APP_LOGGING_CONTEXTS
            }
        }
    }

    // Output current configuration (development only)
    logConfig() {
        if (process.env.NODE_ENV === 'development') {
            console.group('🔧 Logger Configuration')
            console.table(this.getConfig())
            console.groupEnd()
        }
    }
}

export const logger = new Logger()
export default logger